import { parseStringWithSeparator, type ParsedTitleDescription } from './parseStringWithSeparator';

/**
 * A list item as it may arrive from the analysis LLM output. The prompt asks
 * for plain "Title<separator>Description" strings, but the model sometimes
 * returns structured objects (e.g. `{ name, description }`) instead, so callers
 * must treat each item as untrusted.
 */
export type RawTitleDescription = string | Record<string, unknown>;

/* eslint-disable i18next/no-literal-string -- property names probed on the raw LLM JSON */
const TITLE_KEYS = ['title', 'name', 'label', 'heading'] as const;
const DESCRIPTION_KEYS = ['description', 'subtitle', 'summary', 'text', 'detail'] as const;
/* eslint-enable i18next/no-literal-string */

const firstNonEmptyString = (obj: Record<string, unknown>, keys: readonly string[]): string => {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim() !== '') {
      return value.trim();
    }
  }
  return '';
};

/**
 * Normalizes an untrusted analysis list item into `{ title, description }`.
 *
 * Plain strings are split on the first separator (see `parseStringWithSeparator`).
 * Object items are mapped from common title/description keys. Any other input
 * (null, number, key-less object) yields empty fields rather than throwing, so
 * unexpected LLM output can never crash the rendering tree.
 *
 * @param raw - The raw list item (string or object) from the LLM.
 * @param separator - Separator used when `raw` is a string (default: ':').
 */
export const normalizeTitleDescription = (
  raw: unknown,
  separator: string = ':',
): ParsedTitleDescription => {
  if (typeof raw === 'string') {
    return parseStringWithSeparator(raw, separator);
  }

  if (raw !== null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    return {
      title: firstNonEmptyString(obj, TITLE_KEYS),
      description: firstNonEmptyString(obj, DESCRIPTION_KEYS),
    };
  }

  return { title: '', description: '' };
};

/**
 * The first three entries that carry a title or description, as a `NumberedList` renders them.
 * A section decides whether the list has data from the same call, so the two cannot disagree.
 */
export const toNumberedListItems = (raw: RawTitleDescription[] | undefined) =>
  (raw ?? [])
    .slice(0, 3)
    .map((item) => normalizeTitleDescription(item, ':'))
    .filter((item) => item.title || item.description)
    .map((item) => ({ label: item.title, text: item.description }));
